/*
 * anual.js — Lógica pura de la declaración anual (C-020, ver diseno/CAMBIOS.md).
 *
 * Por ejercicio, al año siguiente: morales a más tardar el 31 de marzo, físicas el 30 de abril.
 * Pasos en cascada: Cálculo (con revisión de Pedro si el cliente la tiene) → Presentada (o en $0)
 * → Enviada al cliente → Pago (o No aplica). La misma regla vive en backend/anual.js.
 */
import { fechaCorta } from './estados';

export const PASOS_ANUAL = [
  { clave: 'calculo', nombre: 'Cálculo', corto: 'Cálculo' },
  { clave: 'presentada', nombre: 'Presentada', corto: 'Presentada' },
  { clave: 'envio', nombre: 'Enviada al cliente', corto: 'Envío' },
  { clave: 'pago', nombre: 'Pago', corto: 'Pago' },
];
export const ANUAL_TERMINADAS = 'terminadas';

const MS_DIA = 24 * 60 * 60 * 1000;
const inicioDelDia = (f) => new Date(f.getFullYear(), f.getMonth(), f.getDate());

export const necesitaRevision = (cliente) => !!cliente.revision_pedro && cliente.revision_pedro !== 'no';

/** Fecha límite: 31 mar (morales) o 30 abr (físicas) del año siguiente al ejercicio. */
export const fechaLimiteAnual = (tipo, ejercicio) =>
  (tipo === 'fisica' ? new Date(ejercicio + 1, 3, 30) : new Date(ejercicio + 1, 2, 31));

/** Ejercicio que abre la pestaña: el más reciente cuyo plazo ya empezó (en 2026, el 2025). */
export const ejercicioActual = (hoy = new Date()) => hoy.getFullYear() - 1;

const estadoDe = (cliente, clave) => (cliente.pasos && cliente.pasos[clave] ? cliente.pasos[clave].estado : 'rojo');

export const pasoTerminado = (cliente, clave) => {
  const e = estadoDe(cliente, clave);
  if (clave === 'calculo') return necesitaRevision(cliente) ? e === 'azul' : e === 'verde' || e === 'azul';
  if (clave === 'presentada') return e === 'verde' || e === 'rosa';
  if (clave === 'pago') return e === 'verde' || e === 'no_aplica';
  return e === 'verde';
};

/** Índice del primer paso sin terminar (0-3), o 4 si terminó todo. */
export const pasoActualAnual = (cliente) => {
  const i = PASOS_ANUAL.findIndex(p => !pasoTerminado(cliente, p.clave));
  return i === -1 ? PASOS_ANUAL.length : i;
};

export const porRevisarAnual = (cliente) => necesitaRevision(cliente) && estadoDe(cliente, 'calculo') === 'verde';

/** Días de atraso: después de la fecha límite y sin presentar. */
export const diasAtrasoAnual = (cliente, ejercicio, hoy = new Date()) => {
  if (pasoTerminado(cliente, 'presentada')) return 0;
  const d = Math.round((inicioDelDia(hoy) - fechaLimiteAnual(cliente.tipo, ejercicio)) / MS_DIA);
  return d > 0 ? d : 0;
};

/**
 * Cómo se ve un paso: { clave, nombre, tooltip, sello, esperaRevision, atrasado, dias, noAplica,
 * fijo (no se puede tocar), nota ("el 24 mar") }. Las claves son las de la vista mensual
 * (completado, revisado, presentado-cero, en-proceso, por-hacer, bloqueado) o 'no-aplica'.
 */
export const pasoVisibleAnual = (cliente, clavePaso, ejercicio, hoy = new Date()) => {
  const i = PASOS_ANUAL.findIndex(p => p.clave === clavePaso);
  const actual = pasoActualAnual(cliente);
  const e = estadoDe(cliente, clavePaso);
  const dias = i === actual && i <= 1 ? diasAtrasoAnual(cliente, ejercicio, hoy) : 0;
  const guardado = cliente.pasos && cliente.pasos[clavePaso];
  const nota = clavePaso === 'presentada' && (e === 'verde' || e === 'rosa') && guardado?.fecha_actualizacion
    ? `el ${fechaCorta(guardado.fecha_actualizacion, hoy)}` : null;
  const atraso = dias ? ` · atrasada ${dias} ${dias === 1 ? 'día' : 'días'}` : '';
  const v = { atrasado: dias > 0, dias, sello: null, esperaRevision: false, noAplica: false, fijo: false, nota };
  if (clavePaso === 'pago' && e === 'no_aplica') {
    const porCero = estadoDe(cliente, 'presentada') === 'rosa';
    return { ...v, clave: 'no-aplica', noAplica: true, fijo: porCero, tooltip: porCero ? 'No aplica: se presentó en $0' : 'No aplica: este año no sale a pagar' };
  }
  if (clavePaso === 'presentada' && e === 'rosa') return { ...v, clave: 'presentado-cero', tooltip: 'Presentada en $0' };
  if (e === 'azul') return { ...v, clave: 'revisado', sello: 'revisado', tooltip: 'Revisado por Pedro' };
  if (e === 'verde') {
    if (clavePaso === 'calculo' && porRevisarAnual(cliente)) {
      return { ...v, clave: 'completado', sello: 'por-revisar', tooltip: `Por revisar: falta que Pedro lo revise${atraso}` };
    }
    return { ...v, clave: 'completado', tooltip: 'Completado' };
  }
  if (i > actual) {
    const espera = i === 1 && actual === 0 && porRevisarAnual(cliente);
    return {
      ...v, clave: 'bloqueado', esperaRevision: espera,
      tooltip: espera ? 'Bloqueado: espera la revisión de Pedro' : `Bloqueado: falta ${PASOS_ANUAL[actual].nombre.toLowerCase()}`,
    };
  }
  if (e === 'amarillo') return { ...v, clave: 'en-proceso', tooltip: `En proceso${atraso}` };
  return { ...v, clave: 'por-hacer', tooltip: `Por hacer${atraso}` };
};

/** Siguiente estado al hacer clic. `marcaRevisado`: el Supervisor. */
export const siguienteEstadoAnual = (cliente, clavePaso, marcaRevisado) => {
  const e = estadoDe(cliente, clavePaso);
  if (clavePaso === 'calculo' && e === 'verde' && necesitaRevision(cliente) && marcaRevisado) return 'azul';
  const ciclos = {
    calculo: { rojo: 'amarillo', amarillo: 'verde', verde: 'rojo', azul: 'rojo' },
    presentada: { rojo: 'amarillo', amarillo: 'verde', verde: 'rosa', rosa: 'rojo' },
    envio: { rojo: 'amarillo', amarillo: 'verde', verde: 'rojo' },
    pago: { rojo: 'amarillo', amarillo: 'verde', verde: 'no_aplica', no_aplica: 'rojo' },
  };
  return ciclos[clavePaso][e] || 'amarillo';
};

/** Tarjeta de un grupo: { total, presentadas, enviadas, pagadas, porRevisar, atrasadas } */
export const resumenGrupo = (clientes, ejercicio, hoy = new Date()) => ({
  total: clientes.length,
  presentadas: clientes.filter(c => pasoTerminado(c, 'presentada')).length,
  enviadas: clientes.filter(c => pasoTerminado(c, 'envio')).length,
  pagadas: clientes.filter(c => pasoTerminado(c, 'pago')).length,
  porRevisar: clientes.filter(porRevisarAnual).length,
  atrasadas: clientes.filter(c => diasAtrasoAnual(c, ejercicio, hoy) > 0).length,
});
